import React, { useState, useRef, useEffect } from 'react';
import { UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { CKEditor, CKEditorContext } from '@ckeditor/ckeditor5-react';
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';
import { toast } from 'react-toastify';
import { upload_image } from 'Api/profile';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { gen_grn_invoice, handle_send } from 'Api/Invoice';
import { isValidEmail } from 'helpers/Enums/Enums';
import BaseButton from 'Components/Base/BaseButton';
import { checkStatusCodeSuccess, DISCARD_LABEL, SEND_LABEL, SUBJECT_LABEL } from 'Components/constants/Common';
import BaseInput from 'Components/Base/BaseInput';
import { InputPlaceHolder } from 'Components/constants/validation';
import { EmailLabel } from 'Components/constants/Staff';
import { viewPurchaseOrder } from 'Api/PurchaseOrder';
import { FaFileInvoice } from 'react-icons/fa'; 

interface URLParams {
  id: string;
  version: string;
}

interface EmailChipProps {
  email: string;
  onRemove: (email: string) => void;
}

const EmailChip: React.FC<EmailChipProps> = ({ email, onRemove }) => (
  <span className="badge bg-light text-dark me-1">
    {email}
    <button type="button" className="btn-close ms-1" aria-label="Close" onClick={() => onRemove(email)}></button>
  </span>
);

interface EmailInputProps {
  emails: string[];
  onAddEmail: (email: string) => void;
  onRemoveEmail: (email: string) => void;
  placeholder: string;
}

interface EmailPayload {
  to: string;
  cc: string;
  bcc: string;
  subject: string;
  body: string;
  attach_file: File | null;  // Changed from attachments: File[]
}

const EmailInput: React.FC<EmailInputProps> = ({ emails, onAddEmail, onRemoveEmail, placeholder }) => {
  const [inputValue, setInputValue] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === 'Tab') && inputValue.trim()) {
      e.preventDefault();
      onAddEmail(inputValue.trim());
      setInputValue('');
    }
  };

  const handleWrapperClick = () => {
    inputRef.current?.focus();
  };

  return (
    <div 
      className="form-control d-flex flex-wrap align-items-center"
      onClick={handleWrapperClick}
      style={{ minHeight: '38px', height: 'auto', cursor: 'text' }}
    >
      {emails.map(email => (
        <EmailChip key={email} email={email} onRemove={onRemoveEmail} />
      ))}
      <input
        ref={inputRef}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        placeholder={emails.length === 0 ? placeholder : ''}
        style={{ border: 'none', outline: 'none', flexGrow: 1, minWidth: '50px' }}
      />
    </div>
  );
};
const EmailComposePageGRN: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { version } = useParams<{ version: string }>();
  const [showCc, setShowCc] = useState<boolean>(false);
  const [showBcc, setShowBcc] = useState<boolean>(false);
  const [toEmails, setToEmails] = useState<string[]>([]);
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [bccEmails, setBccEmails] = useState<string[]>([]);
  const [btnLoader ,setBtnLoader] = useState<boolean>(false);
  const [subject, setSubject] = useState<string>(`GRN Confirmation For PO-${id} | Receipt-${version}`);
  const [emailError ,setEmailError] = useState<string>('');
  const [body, setBody] = useState<string>(`
            <pre>Dear ,<br><br>We are writing to confirm the Goods Receipt Note (GRN) for <strong>PO-${id}, Receipt-${version}</strong>.<br><br>Please find the attached receipt for your reference.<br><br><strong>Please note:</strong> This GRN confirms only the receipt of goods and does not confirm the pricing details.<br><br>Should you have any questions or concerns, please do not hesitate to reach out to us.<br><br>Thank you for your cooperation.<br><br> Best regards,<br><strong>Machine Elements</strong>
            </pre>
          `);
  const [attachment, setAttachment] = useState<File | null>(null);  // Changed from attachments
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceGenerated, setInvoiceGenerated] = useState<boolean>(false); // State to track invoice generation
  const [invoiceLoading, setInvoiceLoading] = useState<boolean>(false); 
  const navigate = useNavigate();

  const handleEditorChange = (event: any, editor: any) => {
    const data = editor.getData();
    setBody(data);
  };

  useEffect(() => {
    const fetchInvoice = async () => {
      if (!id || !version) {
        toast.error('Missing required parameters');
        return;
      }
      
      setIsLoading(true);
      try {
        const blob = await generateOrderInvoice(id!, version!);
        if (blob) {
          const file = new File([blob], `GRN_invoice_${id}.pdf`, { type: 'application/pdf' });
          setAttachment(file);
          setInvoiceGenerated(true); // Set invoice as generated
        }
      } catch (error) {
        toast.error('Failed to fetch invoice file');
      } finally {
        setIsLoading(false);
      }
    };
    fetchOrderDetails();
    fetchInvoice();
  }, [id, version]);

  const fetchOrderDetails = () => {
    setIsLoading(true);
    viewPurchaseOrder(Number(id))
      .then((resp) => {
        if (checkStatusCodeSuccess(resp?.statusCode)) {
          setToEmails([resp?.data?.authUser?.email]);
          setBody(`
            <pre>Dear ${resp?.data?.authUser?.name},<br><br>We are writing to confirm the Goods Receipt Note (GRN) for <strong>PO-${id}, Receipt-${version}</strong>.<br><br>Please find the attached receipt for your reference.<br><br><strong>Please note:</strong> This GRN confirms only the receipt of goods and does not confirm the pricing details.<br><br>Should you have any questions or concerns, please do not hesitate to reach out to us.<br><br>Thank you for your cooperation.<br><br> Best regards,<br><strong>Machine Elements</strong>
            </pre>
          `);
          
        } else {
          toast.error(resp?.message);
        }
      })
      .catch((err) => {
        toast.error('Failed to fetch order details');
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  async function generateOrderInvoice(orderId: string, version: string) {
    setInvoiceLoading(true);
    try {
      const response = await gen_grn_invoice(orderId, version, { responseType: 'blob' });
      return response;
    } catch (error) {
      toast.error('Failed to generate invoice');
      return null;
    } finally{
      setInvoiceLoading(false);
    }
  }

  const handleSend = async () => {
    setBtnLoader(true);
    const formData = new FormData();
    formData.append('to', toEmails.join(','));
    formData.append('cc', ccEmails.join(','));
    formData.append('bcc', bccEmails.join(','));
    formData.append('subject', subject);
    formData.append('description', body);
    
    if (attachment) {
      formData.append('attach_file', attachment);  
    }

    try {
      const response = await handle_send(formData);
      toast.success('Email sent successfully');
      navigate('/purchase-order');
    } catch (error) {
      toast.error('Failed to send email');
    } finally {
      setBtnLoader(false);
    }
  };

  const handleDiscard = () => {
    setToEmails([]);
    setCcEmails([]);
    setBccEmails([]);
    setSubject('');
    setBody('');
    setAttachment(null);  // Reset single attachment
    setShowCc(false);
    setShowBcc(false);
    navigate('/purchase-order');
  };

  const addEmail = (type: 'to' | 'cc' | 'bcc', email: string) => {
    if (isValidEmail(email)) {
      setEmailError("");
      switch (type) {
        case 'to':
          setToEmails([...toEmails, email]);
          break;
        case 'cc':
          setCcEmails([...ccEmails, email]);
          break;
        case 'bcc':
          setBccEmails([...bccEmails, email]);
          break;
      }
    } else {
      setEmailError('Please Enter valid email and press enter');
    }
  };

  const removeEmail = (type: 'to' | 'cc' | 'bcc', email: string) => {
    switch (type) {
      case 'to':
        setToEmails(toEmails.filter(e => e !== email));
        break;
      case 'cc':
        setCcEmails(ccEmails.filter(e => e !== email));
        break;
      case 'bcc':
        setBccEmails(bccEmails.filter(e => e !== email));
        break;
    }
  };

  const [generatingInvoice, setGeneratingInvoice] = useState<boolean>(false); // State to track invoice generation loading

  const handleGenerateInvoice = async () => {
    setGeneratingInvoice(true);
    await generateOrderInvoice(id!, version!);
    setGeneratingInvoice(false);
  };

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="card-header">
                <h4 className="card-title mb-0">New Message</h4>
              </div>
              <div className="card-body">
                <form onSubmit={(e) => { e.preventDefault(); handleSend(); }}>
                  <div className="mb-3 position-relative">
                    <label>To:</label>
                    <EmailInput
                      emails={toEmails}
                      onAddEmail={(email) => addEmail('to', email)}
                      onRemoveEmail={(email) => removeEmail('to', email)}
                      placeholder={InputPlaceHolder(EmailLabel) + ' and press enter'}
                    />
                    <p className='text-danger'>{emailError}</p>
                    <div className="position-absolute top-0 end-0">
                      <div className="d-flex">
                        <BaseButton
                          className="btn btn-sm text-dark border-0 bg-light fw-semibold px-2"
                          type="button"
                          onClick={() => setShowCc(!showCc)}>
                          Cc
                        </BaseButton>
                        <BaseButton
                          className="btn btn-sm text-dark border-0 bg-light fw-semibold px-2"
                          type="button"
                          onClick={() => setShowBcc(!showBcc)}>
                          Bcc
                        </BaseButton>
                      </div>
                    </div>
                  </div>
                  {showCc && (
                    <div className="mb-3">
                      <label>Cc:</label>
                      <EmailInput
                        emails={ccEmails}
                        onAddEmail={(email) => addEmail('cc', email)}
                        onRemoveEmail={(email) => removeEmail('cc', email)}
                        placeholder={InputPlaceHolder(EmailLabel)}
                      />
                    </div>
                  )}
                  {showBcc && (
                    <div className="mb-3">
                      <label>Bcc:</label>
                      <EmailInput
                        emails={bccEmails}
                        onAddEmail={(email) => addEmail('bcc', email)}
                        onRemoveEmail={(email) => removeEmail('bcc', email)}
                        placeholder={InputPlaceHolder(EmailLabel)}
                      />
                    </div>
                  )}
                  <div className="mb-3">
                    <BaseInput
                      type='text'
                      className='form-control'
                      placeholder={InputPlaceHolder(SUBJECT_LABEL)}
                      value={subject}
                      onChange={(e) => setSubject(e.target.value)}
                    />
                  </div>
                  <div className="mb-3">
                    <CKEditor
                      editor={ClassicEditor}
                      data={body}
                      onChange={handleEditorChange}
                      config={{
                        toolbar: ['bold', 'italic', 'underline', '|', 'undo', 'redo', '|'],
                        ckfinder: {
                          uploadUrl: '' // Replace with your actual file upload endpoint
                        }
                      }}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Attachment:</label>
                    {isLoading && (
                      <div className="mt-2">
                       
                        <span className="ms-2">Fetching invoice...</span>
                      </div>
                    )}
                    {attachment && (
                      <div className="mt-2">
                        <span>{attachment.name} ({(attachment.size / 1024).toFixed(2)} KB)</span>
                      </div>
                    )}
                    {!invoiceGenerated && (
                      <BaseButton
                        type='button'
                        className='btn btn-primary mt-2'
                        onClick={handleGenerateInvoice} 
                       loader={invoiceLoading}
                      >
                       
                            <FaFileInvoice className="me-1" />
                            Generate Invoice
                      </BaseButton>
                    )}
                  </div>

                  <div className="mt-4">
                    <div className="d-flex gap-2">
                    <BaseButton
                        className='btn btn-success'
                        type='submit'
                        loader={btnLoader}
                        disable={!invoiceGenerated}> 
                        {SEND_LABEL}
                      </BaseButton>
                      <BaseButton
                        type='button'
                        className='btn btn-danger'
                        onClick={handleDiscard}>
                        {DISCARD_LABEL}
                      </BaseButton>
                     
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
export default EmailComposePageGRN;